/**
 * Nav active-link sync — keeps `aria-current="page"` and the active styling on
 * the correct nav link across Swup navigations.
 *
 * The site header/nav renders OUTSIDE `#swup`, so its server-rendered active
 * state (set via `{{ if is_current }}` in the nav partials) freezes on the first
 * page and points at the wrong link after every soft navigation. This re-derives
 * the active link from the URL on the canonical psd:page-view signal.
 *
 * Server-first: the correct link ships active in the initial HTML; this only
 * corrects it after client-side navigations. Markup contract (per nav link):
 *   - `data-nav-link`            — opt this link into the sync
 *   - `data-nav-active`   (opt)  — space-separated classes present ONLY when current
 *   - `data-nav-inactive` (opt)  — space-separated classes present ONLY when NOT current
 * The class lists live in the template so this module never hard-codes a design
 * (desktop nav and the mobile drawer use different active styles).
 */

import { PAGE_VIEW_EVENT } from '../router/swup.js';

function classList(link, key) {
    return (link.dataset[key] || '').split(/\s+/).filter(Boolean);
}

function isCurrent(link, pathname) {
    try {
        return new URL(link.href).pathname === pathname;
    } catch {
        return false;
    }
}

/**
 * Re-sync every `[data-nav-link]` to the current location. Idempotent — safe to
 * run on load and on every page:view.
 *
 * @returns {void}
 */
export function syncNavActive() {
    const pathname = location.pathname;

    for (const link of document.querySelectorAll('[data-nav-link]')) {
        const active = classList(link, 'navActive');
        const inactive = classList(link, 'navInactive');

        if (isCurrent(link, pathname)) {
            link.setAttribute('aria-current', 'page');
            link.classList.add(...active);
            link.classList.remove(...inactive);
        } else {
            if (link.getAttribute('aria-current') === 'page') {
                link.removeAttribute('aria-current');
            }
            link.classList.remove(...active);
            link.classList.add(...inactive);
        }
    }
}

/**
 * Wire the sync: run once now, then re-run on every Swup navigation.
 *
 * TWICE PER VISIT, DELIBERATELY, AND THE EXTRA PASS IS INSURANCE RATHER THAN A
 * FIX. Measured on this site: Swup pushes history at ~70ms, the content swap
 * (`content:replace`) lands at ~653ms, and `page:view` — the hook behind
 * PAGE_VIEW_EVENT — at ~660ms. So syncing on PAGE_VIEW_EVENT alone was never
 * visibly wrong: the highlight trailed the ADDRESS BAR by half a second, but it
 * moved within 7ms of the content it describes, and until that swap the visitor is
 * still looking at the page the old highlight correctly names.
 *
 * What the pair buys is that the nav is pinned to the swap rather than to the
 * settle. `content:replace` is the earliest correct moment — history is already
 * pushed, so `location.pathname` is the destination — and pinning there means any
 * future post-swap animation lengthens the gap between content and settle without
 * dragging the nav along behind it.
 *
 * The PAGE_VIEW_EVENT listener stays as the backstop: it covers the initial load
 * dispatch and any visit that reaches settle without a content swap. syncNavActive
 * is idempotent and derives everything from the URL, so running twice costs one
 * extra pass over a handful of links.
 *
 * NOTE FOR TESTS: neither hook helps a spec that waits on `waitForURL` and then
 * asserts once. That resolves at the 70ms history push, ~580ms before either sync
 * runs, so the assertion must retry (`expect(locator)`), not read-and-compare.
 * e2e/tests/nav-active-sync.spec.ts failed for exactly that reason.
 *
 * @param {import('swup').default} [router] — the live router, when available.
 *   Omitted (no-JS-router contexts, tests) the sync still runs on load and on
 *   PAGE_VIEW_EVENT, just without the early pass.
 * @returns {void}
 */
export function initNavActiveSync(router) {
    syncNavActive();
    window.addEventListener(PAGE_VIEW_EVENT, syncNavActive);
    router?.hooks?.on('content:replace', syncNavActive);
}
